import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { ApiService } from '../../core/services/api/api.service';

// ─── NEW: consent preferences (personalized rewards / updates / feedback) ─
// Sent as part of SignupPayload.consents — all default to false and are
// entirely optional (see SignupComponent.initForm() and the "Stay in the
// loop" section in signup.component.html).
export interface ConsentPreferences {
  personalizedRewards: boolean;
  stayUpdated: boolean;
  shareFeedback: boolean;
}

export interface SignupPayload {
  qrToken: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  referralCode?: string | null;
  // NEW: optional consent preferences, unchecked by default. See
  // ConsentPreferences above and SignupComponent.onSubmit().
  consents?: ConsentPreferences;
}

export interface PageAccess {
  pageId: number;
  name: string;
  description: string | null;
}

export interface LoginPayload {
  otp: string;
  email?: string;
  phone?: string;
  customerQRToken?: string;
  expectedVendorId?: string;
  captchaToken?: string;
}

export interface SendOtpPayload {
  email?: string;
  phone?: string;
  customerQRToken?: string;
  expectedVendorId?: string;
}

// ─── NEW: lookup-phone response shape ────────────────────────────────────
export interface LookupPhonePrefill {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface LookupPhoneData {
  exists: boolean;
  alreadyLinked?: boolean;
  userId?: string;
  prefill?: LookupPhonePrefill;
}

export interface LookupPhoneResponse {
  success: boolean;
  data?: LookupPhoneData;
  message?: string;
}

// ─── NEW: multi-role support ──────────────────────────────────────────────
export interface AuthUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  vendorId?: string;
  vendorName?: string;
  /** Every role this user is allowed to act as, e.g. ["vendor", "customer"] */
  availableRoles?: string[];
  /** Whether the switch-role button should be shown at all */
  canSwitchRole?: boolean;
  pages: PageAccess[];
  [key: string]: any;
}

export interface SwitchRoleResponse {
  success: boolean;
  message?: string;
  data?: {
    accessToken: string;
    sessionId: string;
    expiresIn?: number;
    user: AuthUser;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /**
   * Emits every time the auth state changes (login or logout).
   * The sidebar subscribes to this to rebuild its filtered menu.
   * Emits `true` when logged in, `false` when logged out.
   */
  private _authState = new BehaviorSubject<boolean>(this.isLoggedIn());
  readonly authState$ = this._authState.asObservable();


  private _lastLoginData: any = null;
  get lastLoginData(): any { return this._lastLoginData; }

  constructor(private http: HttpClient, private apiService: ApiService) {}

  // ─── Session storage ───────────────────────────────────────────────────────

  /**
   * Call this right after a successful login response.
   * Saves token, sessionId, role, and the full pages array.
   * Then notifies authState$ so the sidebar re-filters immediately.
   */
  storeSession(data: any): void {
    localStorage.setItem('access_token', data.accessToken);
    localStorage.setItem('session_id',   data.sessionId);
    localStorage.setItem('user_role',    data.user.role);
    localStorage.setItem('user_pages',   JSON.stringify(data.user.pages ?? []));
    // Multi-role support — persisted so the role-switch button can read it
    // without needing the full login response in memory.
    localStorage.setItem('available_roles', JSON.stringify(data.user.availableRoles ?? [data.user.role]));
    localStorage.setItem('can_switch_role',  JSON.stringify(!!data.user.canSwitchRole));
    // keep legacy 'user' key so other parts of the app still work
    localStorage.setItem('user',         JSON.stringify(data.user));
    this._authState.next(true);   // ← triggers sidebar rebuild
  }

  removeToken(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('session_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_pages');
    localStorage.removeItem('available_roles');
    localStorage.removeItem('can_switch_role');
    localStorage.removeItem('csrf_token');
    localStorage.removeItem('user');
    localStorage.removeItem('vendor');
    localStorage.removeItem('pages');
    this._authState.next(false);  // ← triggers sidebar rebuild
  }

  // ─── Token / session helpers ───────────────────────────────────────────────

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  setToken(token: string): void {
    if (token) localStorage.setItem('access_token', token);
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return !!token && token !== 'undefined' && token !== 'null';
  }

  // ─── JWT expiry helpers ─────────────────────────────────────────────────

  /** Decodes a JWT payload client-side. No signature check — just reads `exp`. */
  private decodeToken(token: string): { exp?: number; [key: string]: any } | null {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('')
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  /**
   * True if `token` (defaults to the stored access token) is missing,
   * malformed, or past its `exp` claim. `skewSeconds` treats a token as
   * expired a little early so we don't race a request that lands right
   * on the boundary.
   */
  isTokenExpired(token: string | null = this.getToken(), skewSeconds = 10): boolean {
    if (!token || token === 'undefined' || token === 'null') return true;
    const payload = this.decodeToken(token);
    if (!payload?.exp) return true; // no exp claim → can't trust it
    return Date.now() >= payload.exp * 1000 - skewSeconds * 1000;
  }

  /**
   * Called once at app startup (see auth.initializer.ts), before the router
   * activates any route — so pageGuard never has to guess at a stale token.
   *
   *  - Nothing persisted at all              → resolves false.
   *  - Access token still valid               → restores state, resolves true,
   *                                              no network call needed.
   *  - Access token expired but a session id
   *    is on record                           → silently calls
   *                                              /auth/refresh-token. This is
   *                                              what lets a session survive
   *                                              up to a week of the browser
   *                                              being closed and reopened,
   *                                              as long as the backend
   *                                              session is still valid.
   *  - Refresh fails (backend session/refresh
   *    validity truly expired)                → clears auth data, resolves
   *                                              false, so callers redirect
   *                                              to /login.
   *
   * Never throws.
   */
  initializeAuth(): Observable<boolean> {
    const token = this.getToken();
    const sessionId = this.getSessionId();

    if (!token && !sessionId) {
      return of(false);
    }

    if (token && !this.isTokenExpired(token)) {
      this._authState.next(true);
      return of(true);
    }

    if (sessionId) {
      return this.refreshToken().pipe(
        switchMap(res => {
          const ok = !!res?.data?.accessToken;
          if (ok) this._authState.next(true);
          return of(ok);
        }),
        catchError(() => {
          this.removeToken();
          return of(false);
        })
      );
    }

    this.removeToken();
    return of(false);
  }

  getSessionId(): string | null {
    return localStorage.getItem('session_id');
  }

  getUserRole(): string {
    return localStorage.getItem('user_role') ?? '';
  }

  /** All roles this user can act as, e.g. ['vendor', 'customer']. */
  getAvailableRoles(): string[] {
    try {
      return JSON.parse(localStorage.getItem('available_roles') ?? '[]');
    } catch {
      return [];
    }
  }

  /** Whether the switch-role control should be shown at all. */
  canSwitchRole(): boolean {
    return localStorage.getItem('can_switch_role') === 'true'
      && this.getAvailableRoles().length > 1;
  }

  /** Roles the user could switch TO right now (excludes the active one). */
  getSwitchableRoles(): string[] {
    const current = this.getUserRole();
    return this.getAvailableRoles().filter(r => r !== current);
  }

  getUserPages(): PageAccess[] {
    try {
      return JSON.parse(localStorage.getItem('user_pages') ?? '[]');
    } catch {
      return [];
    }
  }

  /**
   * Returns true if the logged-in user's pages list contains pageName.
   * pageName must exactly match the `name` field from the login API response.
   */
  hasAccess(pageName: string): boolean {
    return this.getUserPages().some(p => p.name === pageName);
  }

  getAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this.getToken()}` });
  }

  getUser(): any {
    try { return JSON.parse(localStorage.getItem('user') ?? 'null'); } catch { return null; }
  }

  getVendor(): any {
    try { return JSON.parse(localStorage.getItem('vendor') ?? 'null'); } catch { return null; }
  }

  sendOtp(payload: SendOtpPayload): Observable<any> {
    return this.http.post(this.apiService.getApiUrl('/shopasst/api/auth/send-otp'), payload);
  }

  /**
   * Looks up whether a phone number is already registered.
   * Used by the signup form to auto-fill known-user details as soon as
   * a valid 10-digit phone number has been entered.
   *
   * Response shape:
   * {
   *   success: true,
   *   data: {
   *     exists: boolean,
   *     alreadyLinked: boolean,
   *     userId: string,
   *     prefill: { firstName, lastName, email, phone }
   *   }
   * }
   */
  lookupPhone(phone: string): Observable<LookupPhoneResponse> {
    return this.http.post<LookupPhoneResponse>(
      this.apiService.getApiUrl('/shopasst/api/auth/lookup-phone'),
      { phone }
    );
  }
  
  googleAuth(googleIdToken: string): Observable<any> {
    // Step 1: Fetch CSRF token first (anonymous, just needs the cookie session)
    return this.http
      .get<any>(
        this.apiService.getApiUrl('/shopasst/api/auth/csrf-token'),
        { withCredentials: true }
      )
      .pipe(
        tap(csrfRes => {
          // Save CSRF token so interceptor can pick it up, OR we send it manually below
          if (csrfRes?.token) {
            localStorage.setItem('csrf_token', csrfRes.token);
          }
        }),
        switchMap(csrfRes => {
          const csrfToken = csrfRes?.token ?? localStorage.getItem('csrf_token') ?? '';

          // Step 2: Call gauth WITH the CSRF token in the header
          return this.http.post<any>(
            this.apiService.getApiUrl('/shopasst/api/auth/gauth'),
            { googleIdToken },
            {
              withCredentials: true,
              headers: { 'X-CSRF-Token': csrfToken }
            }
          );
        }),
        tap(loginRes => {
          // Step 3: Store lastLoginData — storeSession() called by component
          if (loginRes?.data?.accessToken) {
            this._lastLoginData = loginRes.data;
          }
        }),
        catchError(err => {
          console.error('Google auth failed:', err);
          throw err;
        })
      );
  }


  login(payload: any): Observable<any> {
    return this.http
      .post<any>(this.apiService.getApiUrl('/shopasst/api/auth/login'), payload, {
        withCredentials: true
      })
      .pipe(
        tap(loginRes => {
          if (loginRes?.data?.accessToken) {
            this._lastLoginData = loginRes.data;

            // storeSession() persists token/session/role/pages AND
            // availableRoles/canSwitchRole, then fires authState$ so the
            // sidebar and the role-switch button pick up the new session.
            this.storeSession(loginRes.data);

            if (loginRes.data.user?.vendor) {
              localStorage.setItem('vendor', JSON.stringify(loginRes.data.user.vendor));
            }
          }
        }),
        switchMap(loginRes => {
          if (loginRes?.success) {
            return this.http.get<any>(
              this.apiService.getApiUrl('/shopasst/api/auth/csrf-token'),
              { withCredentials: true }
            ).pipe(
              tap(csrfRes => {
                if (csrfRes?.token) localStorage.setItem('csrf_token', csrfRes.token);
              }),
              // ✅ KEY FIX: if CSRF fails for any reason, don't error the whole stream
              catchError(() => of({ csrfFailed: true }))
            );
          }
          return of(loginRes);
        })
      );
  }

  /**
   * Switches the current session to a different role the user is entitled
   * to (e.g. vendor <-> customer) WITHOUT requiring a fresh login/OTP.
   *
   * Expected response shape (same family as /login):
   * {
   *   success: true,
   *   data: { accessToken, sessionId, expiresIn, user: { role, pages, ... } }
   * }
   *
   * On success this immediately re-applies storeSession() so the new
   * token/role/pages take effect and authState$ fires — the sidebar and
   * any role-gated UI rebuild automatically, same as after login.
   */
  switchRole(role: string): Observable<SwitchRoleResponse> {
    return this.http.post<SwitchRoleResponse>(
      this.apiService.getApiUrl('/shopasst/api/auth/switch-role'),
      { role },
      { withCredentials: true }
    ).pipe(
      tap(res => {
        if (res?.success && res?.data?.accessToken) {
          this.storeSession(res.data);
        }
      })
    );
  }

  signup(payload: SignupPayload): Observable<any> {
    const url = this.apiService.getApiUrl('/shopasst/api/auth/register');
    console.log('Sending signup request to:', url);
    console.log('With payload:', {
      ...payload,
      qrToken: payload.qrToken?.substring(0, 20) + '...' // Don't log full token
    });

    return this.http.post(url, payload);
  }

  validateQRToken(qrToken: string): Observable<any> {
    return this.http.get(this.apiService.getApiUrl('/shopasst/api/auth/validate-qr'), {
      params: { qr: qrToken }
    });
  }

  logout(sessionId: string): Observable<any> {
    return this.http.post(
      this.apiService.getApiUrl('/shopasst/api/auth/logout'),
      { sessionId },
      { withCredentials: true }
    ).pipe(
      tap(() => {
        this.removeToken(); // fires authState$(false) → sidebar rebuilds
        sessionStorage.removeItem('loginVendorId');
        sessionStorage.removeItem('loginFromVendorQR');
        sessionStorage.removeItem('customerQRToken');
      })
    );
  }

  refreshToken(): Observable<any> {
    return this.http.post<any>(
      this.apiService.getApiUrl('/shopasst/api/auth/refresh-token'),
      {},
      { withCredentials: true }
    ).pipe(
      tap(res => {
        const newToken = res?.data?.accessToken;
        if (newToken) localStorage.setItem('access_token', newToken);
      })
    );
  }
}